import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adsApiCall } from "./api";
import { Ticket, CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import ReferralShareCard from "./ReferralShareCard";
import "./ads-creator.css";

/**
 * Public /redeem/:code? landing page. If the code comes from a URL (share
 * link), we auto-submit once the user is authenticated. Anon visitors see
 * a login CTA first — the code is preserved via localStorage so after
 * signup they land back here and auto-claim.
 */
export default function CouponRedeemLanding() {
  const { code: codeParam } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState((codeParam || "").toUpperCase());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {ok, amount} | {error}
  const [authed, setAuthed] = useState(null); // null=unknown, true/false

  // Pre-flight: check login state by pinging /credits (401 on anon)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await adsApiCall.credits();
        if (!cancelled) setAuthed(true);
      } catch (e) {
        if (!cancelled) setAuthed(e?.response?.status === 401 ? false : true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-redeem if code is in URL and user is logged in
  useEffect(() => {
    if (authed === true && codeParam && !result && !busy) {
      redeem(codeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, codeParam]);

  async function redeem(c) {
    const trimmed = (c || code || "").trim();
    if (!trimmed) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await adsApiCall.redeemCoupon(trimmed);
      setResult({ ok: true, amount: r.amount, code: r.code, my_referral: r.my_referral, team_bonus: r.team_bonus });
      toast.success(`+${r.amount} credits unlocked!`);
      try { localStorage.removeItem("trp_pending_coupon"); } catch { /* ignore */ }
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 401 ? "Please log in first"
        : status === 404 ? "Code not found"
        : status === 409 ? "This code has already been redeemed"
        : status === 410 ? "This code has expired or been deactivated"
        : "Couldn't redeem this code";
      setResult({ ok: false, error: msg, status });
      if (status === 401) {
        // Stash code so post-login we can auto-redeem
        try { localStorage.setItem("trp_pending_coupon", trimmed); } catch { /* ignore */ }
      }
    } finally { setBusy(false); }
  }

  const bg = "linear-gradient(135deg,#1a0c1f 0%,#120b1c 50%,#0b0b12 100%)";

  return (
    <div className="ads-root" data-testid="coupon-redeem-landing" style={{ minHeight: "100vh", background: bg }}>
      <Toaster position="top-right" theme="dark" />
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "80px 24px 48px" }}>
        <Link to="/ads-creator" style={{ color: "#8b8ba7", fontSize: 13, textDecoration: "none" }}>
          ← TopRankPulse Studio
        </Link>

        <div style={{ marginTop: 40, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 72, height: 72, borderRadius: "50%",
            background: "linear-gradient(135deg,#a855f7,#7c3aed)",
            boxShadow: "0 16px 48px rgba(168,85,247,0.35)",
            marginBottom: 20,
          }}>
            <Ticket size={32} color="#fff" />
          </div>
          <h1 style={{ color: "#fff", fontSize: 36, fontWeight: 900, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
            Claim your credits
          </h1>
          <p style={{ color: "#a5a5c0", fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            Enter your TopRankPulse coupon code to top-up your Ads Creator credits.
          </p>
        </div>

        <div
          style={{
            marginTop: 36, padding: 24,
            background: "rgba(20,20,35,0.8)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 16,
            backdropFilter: "blur(12px)",
          }}
        >
          {result?.ok ? (
            <div style={{ textAlign: "center", padding: "16px 8px" }} data-testid="coupon-redeem-success">
              <CheckCircle2 size={56} color="#4ade80" style={{ margin: "0 auto 14px" }} />
              <div style={{ color: "#fff", fontSize: 28, fontWeight: 900, letterSpacing: "-0.01em" }}>
                +{result.amount} credits
              </div>
              <div style={{ color: "#a5a5c0", fontSize: 14, margin: "6px 0 22px" }}>
                Code <span style={{ fontFamily: "monospace", color: "#c084fc" }}>{result.code}</span> redeemed successfully.
              </div>
              {result.team_bonus && result.team_bonus.recipients > 0 && (
                <div
                  data-testid="redeem-team-bonus-pill"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "6px 14px", borderRadius: 999,
                    background: "rgba(74,222,128,0.12)",
                    border: "1px solid rgba(74,222,128,0.35)",
                    color: "#4ade80", fontSize: 12, fontWeight: 800,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    marginBottom: 18,
                  }}>
                  🏢 +{result.team_bonus.total} shared with {result.team_bonus.recipients} team-mate{result.team_bonus.recipients === 1 ? "" : "s"}
                </div>
              )}
              {result.my_referral && (
                <div style={{ margin: "0 0 18px", textAlign: "left" }}>
                  <ReferralShareCard referral={result.my_referral} />
                </div>
              )}
              <button
                onClick={() => navigate("/ads-creator/my-ads")}
                className="ads-btn ads-btn-primary"
                style={{ padding: "12px 24px", fontSize: 15 }}
                data-testid="redeem-go-to-ads"
              >
                Start creating ads <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <>
              <label style={{ color: "#e8e8f0", fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8 }}>
                Coupon code
              </label>
              <input
                data-testid="redeem-code-input"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABCD-EFGH-IJKL"
                onKeyDown={(e) => e.key === "Enter" && redeem()}
                autoFocus
                style={{
                  width: "100%", padding: "14px 16px",
                  background: "rgba(0,0,0,0.4)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10,
                  fontFamily: "monospace", fontSize: 18, letterSpacing: "0.08em",
                  boxSizing: "border-box", textAlign: "center",
                }}
              />
              {result?.error && (
                <div
                  data-testid="redeem-error"
                  style={{
                    marginTop: 12, padding: "10px 12px",
                    background: "rgba(248,113,113,0.1)",
                    border: "1px solid rgba(248,113,113,0.3)",
                    borderRadius: 8, color: "#fca5a5", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <XCircle size={16} /> {result.error}
                  {result.status === 401 && (
                    <Link to="/login" style={{ marginLeft: "auto", color: "#fff", fontWeight: 700 }}>
                      Log in →
                    </Link>
                  )}
                </div>
              )}
              <button
                onClick={() => redeem()}
                disabled={busy || !code.trim()}
                className="ads-btn ads-btn-primary"
                style={{ width: "100%", marginTop: 16, padding: "14px", fontSize: 15, fontWeight: 800 }}
                data-testid="redeem-submit-btn"
              >
                {busy ? <><Loader2 className="animate-spin" size={16} /> Redeeming…</>
                  : authed === false ? "Log in & redeem"
                  : "Redeem credits"}
              </button>
              {authed === false && (
                <p style={{ color: "#8b8ba7", fontSize: 12, textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
                  New to TopRankPulse? <Link to="/login" style={{ color: "#c084fc", fontWeight: 700 }}>Create a free account</Link> — we'll auto-apply the code.
                </p>
              )}
            </>
          )}
        </div>

        <div style={{ marginTop: 28, textAlign: "center", color: "#555", fontSize: 12 }}>
          Powered by TopRankPulse · Your credits never expire.
        </div>
      </div>
    </div>
  );
}
